import classnames from 'classnames';
import { FC, HTMLAttributes, ReactNode } from 'react';

import { AngleDownSmall } from '@zooplus/icons';

import styles from './FormControl.module.scss';

export type FormControlProps = HTMLAttributes<HTMLDivElement> & {
  /* The component to be displayed at the end of this input field. This can be any react node, such as
   * a card icon in case of the card number input field, the CVV icon in case of security field,... */
  appendixItems?: ReactNode;
  hasError?: boolean;
  disabled?: boolean;
  focused?: boolean;
  select?: boolean;
  innerClassname?: string;
  notFullWidth?: boolean;
  /* Displays an icon on the right side of the input */
  icon?: React.ReactNode;
};

const FormControl: FC<FormControlProps> = ({
  appendixItems,
  children,
  hasError = false,
  disabled = false,
  focused = false,
  className,
  select,
  innerClassname,
  notFullWidth,
  icon,
  id,
  ...otherProps
}) => {
  let accessibilityProps: object = {
    ['aria-describedby']: `${id}-error-message`,
  };
  if (hasError !== undefined) {
    accessibilityProps = { ...accessibilityProps, ['aria-invalid']: `${hasError}` };
  }

  return (
    <div
      {...accessibilityProps}
      className={classnames('z-form', className, notFullWidth && styles.notFullWidth)}>
      <div className={styles.formElementWrapper}>
        <div
          {...otherProps}
          id={id}
          className={classnames(styles.formElement, 'z-text-input', innerClassname, {
            // z-input can be added back once SHOP-10836 is done.
            'z-select': select,
            [styles.focused]: focused,
            'z-select--disabled': disabled && select,
            'z-input--disabled': disabled && !select,
            'z-text-input--error': hasError,
          })}></div>
        {appendixItems && (
          // Show additional element at the end of this input field, such as the card icon
          // for card input field, CVV icon for security input field,...
          <span className={styles.appendix}>{appendixItems}</span>
        )}
        {select && (
          // Add an arrow down icon if this field is a select field
          <i
            className={classnames(
              'z-select__arrow',
              disabled && 'z-select__arrow--disabled',
              hasError && 'z-select__arrow--error',
            )}>
            {<AngleDownSmall />}
          </i>
        )}
        {!!icon && <div className={styles.formElementIconContainer}>{icon}</div>}
      </div>
      {children}
    </div>
  );
};

export default FormControl;
